import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Edit, Trash2, Filter } from 'lucide-react';
import AppShell from '@/components/AppShell';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Wins = () => {
  const navigate = useNavigate();
  const [wins, setWins] = useState([]);
  const [filteredWins, setFilteredWins] = useState([]);
  const [goals, setGoals] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editingWin, setEditingWin] = useState(null);
  const [reenhancingWin, setReenhancingWin] = useState(null);
  const [newEnhancement, setNewEnhancement] = useState('');
  const [editForm, setEditForm] = useState({
    original_text: '',
    category: '',
    goal_id: ''
  });
  const [freeTierStatus, setFreeTierStatus] = useState(null);

  const categories = [
    'All',
    'Project Delivery',
    'Process Improvement',
    'Leadership',
    'Stakeholder Impact',
    'Problem Solving',
    'Learning & Growth'
  ];

  useEffect(() => {
    fetchWins();
    fetchGoals();
    fetchFreeTierStatus();
  }, []);

  useEffect(() => {
    filterWins();
  }, [wins, searchTerm, categoryFilter, timeFilter]);

  const fetchWins = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/wins`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      const winsData = response.data.wins || response.data;
      setWins(Array.isArray(winsData) ? winsData : []);
    } catch (error) {
      console.error('Failed to fetch wins:', error);
      toast.error('Failed to load wins');
      setWins([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGoals = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/goals`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      setGoals(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch goals:', error);
    }
  };

  const fetchFreeTierStatus = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/wins/free-tier-status`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      setFreeTierStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch free tier status:', error);
    }
  };


  const filterWins = () => {
    let filtered = [...wins];

    if (searchTerm) {
      filtered = filtered.filter(win =>
        (win.original_text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         win.enhanced_text?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(win => win.category === categoryFilter);
    }

    if (timeFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(win => {
        const winDate = new Date(win.win_date);
        switch (timeFilter) {
          case 'week':
            return (now - winDate) / (1000 * 60 * 60 * 24) <= 7;
          case 'month':
            return (now - winDate) / (1000 * 60 * 60 * 24) <= 30;
          case 'quarter':
            return (now - winDate) / (1000 * 60 * 60 * 24) <= 90;
          default:
            return true;
        }
      });
    }

    setFilteredWins(filtered);
  };

  const handleReenhance = async (win) => {
    setReenhancingWin(win);
    setNewEnhancement(''); // Reset
    
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/wins/reenhance`,
        {
          win_id: win.win_id,
          original_text: win.original_text
        },
        {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        }
      );
      
      setNewEnhancement(response.data.enhanced_text);
    } catch (error) {
      console.error('Failed to re-enhance:', error);
      toast.error('Could not get a new version. Try again in a moment.');
      setReenhancingWin(null);
    }
  };

  const handleAcceptNewEnhancement = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.put(
        `${API}/wins/${reenhancingWin.win_id}`,
        { enhanced_text: newEnhancement, use_enhanced: true },
        {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        }
      );
      
      toast.success('Win updated with the new version');
      setReenhancingWin(null);
      setNewEnhancement('');
      fetchWins();
    } catch (error) {
      console.error('Failed to update win:', error);
      toast.error('Failed to update win');
    }
  };

  const handleEdit = (win) => {
    setEditingWin(win);
    setEditForm({
      original_text: win.use_enhanced && win.enhanced_text ? win.enhanced_text : win.original_text,  // Load the version being displayed
      category: win.category || '',
      goal_id: win.goal_id || ''
    });
  };

  const handleUpdateWin = async () => {
    if (!editForm.original_text.trim()) {
      toast.error('Win text cannot be empty');
      return;
    }

    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.put(
        `${API}/wins/${editingWin.win_id}`,
        editForm,
        {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        }
      );
      toast.success('Win updated!');
      setEditingWin(null);
      fetchWins();
    } catch (error) {
      console.error('Failed to update win:', error);
      toast.error('Failed to update win');
    }
  };

  const handleDelete = async (winId) => {
    if (!window.confirm('Are you sure you want to delete this win?')) return;

    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.delete(`${API}/wins/${winId}`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      toast.success('Win deleted');
      fetchWins();
    } catch (error) {
      console.error('Failed to delete win:', error);
      toast.error('Failed to delete win');
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-navy-900 border-r-transparent"></div>
            <p className="mt-4 text-gray-600">Loading wins...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              All Wins
            </h1>
            <p className="text-gray-600">{filteredWins.length} total wins</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5" style={{ color: 'var(--navy-900)' }} />
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              Filters
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>Search</label>
              <Input
                placeholder="Search wins..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>Category</label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.slice(1).map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>Time Period</label>
              <Select value={timeFilter} onValueChange={setTimeFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="quarter">This Quarter</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Wins List */}
        {filteredWins.length === 0 ? (
          <div className="bg-white p-12 rounded-lg border border-gray-200 text-center">
            <p className="text-gray-600">No wins found. Start logging your achievements!</p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="mt-4 px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
            >
              Log Your First Win
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredWins.map((win) => (
              <div
                key={win.win_id}
                className="bg-white p-6 rounded-lg border border-gray-200 hover:border-navy-200 hover:shadow-sm transition-all duration-200"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex gap-2">
                    <span
                      className="text-xs px-3 py-1 rounded font-medium"
                      style={{ backgroundColor: 'var(--cream-100)', color: 'var(--navy-900)' }}
                    >
                      {win.category || 'General'}
                    </span>
                    {win.goal_id && (
                      <span className="text-xs px-3 py-1 rounded font-medium bg-amber-100 text-amber-800">
                        Linked to goal
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500" style={{ fontFamily: 'JetBrains Mono' }}>
                      {new Date(win.win_date).toLocaleDateString()}
                    </span>
                    <button
                      onClick={() => handleEdit(win)}
                      className="p-2 text-gray-400 hover:text-navy-900 transition-colors duration-200"
                      title="Edit win"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(win.win_id)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors duration-200"
                      title="Delete win"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {win.use_enhanced && win.enhanced_text ? win.enhanced_text : win.original_text}
                </p>
                {win.enhanced_text && !freeTierStatus?.is_free_tier && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <button
                      onClick={() => handleReenhance(win)}
                      className="text-xs text-gray-500 hover:text-navy-900 transition-colors cursor-pointer flex items-center gap-1"
                      title="Get a new polished version"
                    >
                      AI polished · Select to run it again
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Edit Win Dialog */}
        <Dialog open={editingWin !== null} onOpenChange={(open) => !open && setEditingWin(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              Edit Win
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
                What did you accomplish?
              </label>
              <Textarea
                value={editForm.original_text}
                onChange={(e) => setEditForm({ ...editForm, original_text: e.target.value })}
                className="min-h-32"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
                Category
              </label>
              <Select 
                value={editForm.category || 'none'} 
                onValueChange={(value) => setEditForm({ ...editForm, category: value === 'none' ? null : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {categories.slice(1).map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
                Link to Goal
              </label>
              <Select 
                value={editForm.goal_id || 'none'} 
                onValueChange={(value) => setEditForm({ ...editForm, goal_id: value === 'none' ? null : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select goal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {goals.map((goal) => (
                    <SelectItem key={goal.goal_id} value={goal.goal_id}>
                      <div className="max-w-md truncate">
                        {goal.title.length > 80 ? goal.title.substring(0, 80) + '...' : goal.title}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editForm.goal_id && editForm.goal_id !== 'none' && (
                <p className="text-xs text-gray-600 mt-2">
                  Selected: {goals.find(g => g.goal_id === editForm.goal_id)?.title}
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleUpdateWin}
                className="flex-1 py-3 rounded-lg font-medium transition-colors duration-200"
                style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
              >
                Save Changes
              </Button>
              <Button
                variant="outline"
                onClick={() => setEditingWin(null)}
                className="px-6"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Re-enhance Dialog */}
      <Dialog open={reenhancingWin !== null} onOpenChange={(open) => !open && setReenhancingWin(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              New polish
            </DialogTitle>
          </DialogHeader>

          {reenhancingWin && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg border border-gray-200">
                <p className="text-xs font-medium text-gray-500 mb-2">CURRENT VERSION</p>
                <p className="text-sm text-gray-700">
                  {reenhancingWin.use_enhanced && reenhancingWin.enhanced_text 
                    ? reenhancingWin.enhanced_text 
                    : reenhancingWin.original_text}
                </p>
              </div>

              {newEnhancement ? (
                <>
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--cream-100)', borderLeft: '4px solid var(--navy-900)' }}>
                    <p className="text-xs font-medium mb-2" style={{ color: 'var(--navy-900)' }}>NEW POLISHED VERSION</p>
                    <p className="text-sm text-gray-700">{newEnhancement}</p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      onClick={handleAcceptNewEnhancement}
                      className="flex-1 py-3 rounded-lg font-medium transition-colors duration-200"
                      style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
                    >
                      Use new version
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setReenhancingWin(null);
                        setNewEnhancement('');
                      }}
                      className="flex-1 py-3"
                    >
                      Keep Current
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-navy-900 border-r-transparent"></div>
                  <p className="mt-4 text-gray-600">Polishing your win...</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </AppShell>
  );
};

export default Wins;